// @ts-nocheck
/**
 * Data Routing Dashboard
 * Epic 75 - Work Item 1: Intelligent Data Routing System
 *
 * Provides admin interface for:
 * - Storage tier utilization visualization
 * - Performance metrics monitoring
 * - Data tier management and migrations
 * - Real-time alerts and notifications
 */

import React, { useEffect, useState } from 'react';
import {
  Grid,
  Column,
  Heading,
  Tile,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  InlineNotification,
  Loading,
  Tag,
  Modal,
  TextInput,
  Dropdown,
  Stack,
} from '@carbon/react';
import { DonutChart, LineChart, SimpleBarChart } from '@carbon/charts-react';
import '@carbon/charts-react/styles.css';
import { Restart, CheckmarkFilled, WarningAlt, CloudApp, DataBase } from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface TierMetrics {
  tier: string;
  storage: string;
  operations: number;
  avgResponseTime: number;
  cacheHitRate?: number;
}

interface Alert {
  id: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  timestamp: number;
}

interface MigrationCandidate {
  dataType: string;
  key: string;
  currentTier: string;
  recommendedTier: string;
  reason: string;
  accessCount: number;
}

const DataRoutingDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Metrics state
  const [tierMetrics, setTierMetrics] = useState<TierMetrics[]>([]);
  const [totalOperations, setTotalOperations] = useState(0);
  const [avgResponseTime, setAvgResponseTime] = useState(0);
  const [cacheEfficiency, setCacheEfficiency] = useState(0);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  // Migration state
  const [migrationCandidates, setMigrationCandidates] = useState<MigrationCandidate[]>([]);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [selectedMigrations, setSelectedMigrations] = useState<string[]>([]);

  // Chart data
  const [tierDistributionData, setTierDistributionData] = useState<any[]>([]);
  const [performanceTrendData, setPerformanceTrendData] = useState<any[]>([]);
  const [operationCountData, setOperationCountData] = useState<any[]>([]);

  useEffect(() => {
    if (user?.role === 'platform_admin' || user?.role === 'teacher') {
      fetchMetrics();
      fetchAnalysis();
      fetchAlerts();

      // Refresh metrics every 30 seconds
      const interval = setInterval(() => {
        fetchMetrics();
        fetchAlerts();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/data-routing/metrics`, {
        params: { timeRange: '1h', detailed: true },
      });

      const { middlewareMetrics, summary } = response.data;

      // Update summary metrics
      setTotalOperations(summary?.totalOperations || 0);
      setAvgResponseTime(summary?.avgResponseTime || 0);
      setCacheEfficiency(summary?.cacheEfficiency || 0);

      // Process tier metrics
      if (middlewareMetrics?.tierMetrics) {
        const metrics = Object.entries(middlewareMetrics.tierMetrics).map(([tier, data]: [string, any]) => ({
          tier,
          storage: data.storage || tier,
          operations: data.operations || 0,
          avgResponseTime: data.avgResponseTime || 0,
          cacheHitRate: data.cacheHitRate,
        }));
        setTierMetrics(metrics);

        // Prepare donut chart data for tier distribution
        const donutData = metrics.map(m => ({
          group: `${m.tier.toUpperCase()} (${m.storage.toUpperCase()})`,
          value: m.operations,
        }));
        setTierDistributionData(donutData);

        // Prepare bar chart data for operation counts
        const barData = metrics.map(m => ({
          group: m.tier.toUpperCase(),
          value: m.operations,
        }));
        setOperationCountData(barData);
      }

      // Prepare line chart data for performance trends (simulated time series)
      if (middlewareMetrics?.performanceHistory) {
        const lineData = middlewareMetrics.performanceHistory.map((point: any) => ({
          group: 'Response Time',
          date: new Date(point.timestamp),
          value: point.avgResponseTime,
        }));
        setPerformanceTrendData(lineData);
      } else {
        // Fallback: generate sample trend data
        const now = Date.now();
        const trendData = Array.from({ length: 12 }, (_, i) => ({
          group: 'Response Time',
          date: new Date(now - (11 - i) * 300000), // 5-minute intervals
          value: Math.random() * 50 + 10,
        }));
        setPerformanceTrendData(trendData);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Failed to fetch metrics:', err);
      setError(err.response?.data?.message || 'Failed to fetch metrics');
      setLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/data-routing/analysis`);

      const { recommendations } = response.data;

      if (recommendations?.immediateActions) {
        setMigrationCandidates(recommendations.immediateActions);
      }
    } catch (err: any) {
      console.error('Failed to fetch analysis:', err);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/data-routing/alerts`);

      if (response.data?.alerts?.active) {
        setAlerts(response.data.alerts.active);
      }
    } catch (err: any) {
      console.error('Failed to fetch alerts:', err);
    }
  };

  const handleMigration = async () => {
    try {
      const migrationsToProcess = migrationCandidates.filter(m =>
        selectedMigrations.includes(`${m.dataType}-${m.key}`)
      );

      const migrations = migrationsToProcess.map(m => ({
        dataType: m.dataType,
        key: m.key,
        fromTier: m.currentTier,
        toTier: m.recommendedTier,
        reason: m.reason,
      }));

      await axios.post(
        `${API_BASE_URL}/v1/data-routing/migrate`,
        { migrations },
        {
          params: { action: 'queue' },
        }
      );

      setShowMigrationModal(false);
      setSelectedMigrations([]);

      // Refresh data
      fetchMetrics();
      fetchAnalysis();
    } catch (err: any) {
      console.error('Migration failed:', err);
      setError(err.response?.data?.message || 'Migration failed');
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchMetrics();
    fetchAnalysis();
    fetchAlerts();
  };

  if (loading) {
    return <Loading description="Loading data routing metrics..." />;
  }

  // Chart options
  const donutOptions = {
    title: 'Tier Distribution by Operations',
    resizable: true,
    donut: {
      center: {
        label: 'Total Operations',
      },
    },
    height: '300px',
    theme: 'white',
  };

  const lineOptions = {
    title: 'Performance Trends (Avg Response Time)',
    axes: {
      bottom: {
        title: 'Time',
        mapsTo: 'date',
        scaleType: 'time',
      },
      left: {
        title: 'Response Time (ms)',
        mapsTo: 'value',
        scaleType: 'linear',
      },
    },
    curve: 'curveMonotoneX',
    height: '300px',
    theme: 'white',
  };

  const barOptions = {
    title: 'Operations by Tier',
    axes: {
      bottom: {
        title: 'Storage Tier',
        mapsTo: 'group',
        scaleType: 'labels',
      },
      left: {
        title: 'Operation Count',
        mapsTo: 'value',
        scaleType: 'linear',
      },
    },
    height: '300px',
    theme: 'white',
  };

  return (
    <div style={{ padding: '2rem' }}>
      <Stack gap={6}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Heading>Data Routing Dashboard</Heading>
          <Button renderIcon={Restart} onClick={handleRefresh} kind="secondary">
            Refresh Metrics
          </Button>
        </div>

        {/* Error notification */}
        {error && (
          <InlineNotification kind="error" title="Error" subtitle={error} onCloseButtonClick={() => setError(null)} />
        )}

        {/* Active alerts */}
        {alerts.length > 0 && (
          <Stack gap={3}>
            {alerts.map(alert => (
              <InlineNotification
                key={alert.id}
                kind={alert.severity}
                title={`Alert: ${alert.severity.toUpperCase()}`}
                subtitle={alert.message}
                lowContrast
              />
            ))}
          </Stack>
        )}

        {/* Summary metrics */}
        <Grid>
          <Column lg={4} md={4} sm={4}>
            <Tile style={{ padding: '1.5rem', textAlign: 'center' }}>
              <DataBase size={32} style={{ marginBottom: '0.5rem' }} />
              <Heading style={{ fontSize: '2rem', margin: '0.5rem 0' }}>{totalOperations.toLocaleString()}</Heading>
              <p style={{ color: 'var(--cds-text-secondary)' }}>Total Operations</p>
            </Tile>
          </Column>
          <Column lg={4} md={4} sm={4}>
            <Tile style={{ padding: '1.5rem', textAlign: 'center' }}>
              <CloudApp size={32} style={{ marginBottom: '0.5rem' }} />
              <Heading style={{ fontSize: '2rem', margin: '0.5rem 0' }}>{avgResponseTime.toFixed(1)} ms</Heading>
              <p style={{ color: 'var(--cds-text-secondary)' }}>Avg Response Time</p>
            </Tile>
          </Column>
          <Column lg={4} md={4} sm={4}>
            <Tile style={{ padding: '1.5rem', textAlign: 'center' }}>
              <CheckmarkFilled size={32} style={{ marginBottom: '0.5rem' }} />
              <Heading style={{ fontSize: '2rem', margin: '0.5rem 0' }}>{cacheEfficiency.toFixed(1)}%</Heading>
              <p style={{ color: 'var(--cds-text-secondary)' }}>Cache Efficiency</p>
            </Tile>
          </Column>
        </Grid>

        {/* Charts */}
        <Grid>
          <Column lg={8} md={8} sm={4}>
            <Tile>
              <LineChart data={performanceTrendData} options={lineOptions} />
            </Tile>
          </Column>
          <Column lg={8} md={8} sm={4}>
            <Tile>
              <DonutChart data={tierDistributionData} options={donutOptions} />
            </Tile>
          </Column>
        </Grid>

        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Tile>
              <SimpleBarChart data={operationCountData} options={barOptions} />
            </Tile>
          </Column>
        </Grid>

        {/* Tier metrics table */}
        <Tile>
          <Heading style={{ marginBottom: '1rem' }}>Storage Tier Metrics</Heading>
          <DataTable
            rows={tierMetrics.map((m, i) => ({ id: String(i), ...m }))}
            headers={[
              { key: 'tier', header: 'Tier' },
              { key: 'storage', header: 'Storage Type' },
              { key: 'operations', header: 'Operations' },
              { key: 'avgResponseTime', header: 'Avg Response Time (ms)' },
              { key: 'cacheHitRate', header: 'Cache Hit Rate (%)' },
            ]}
          >
            {({ rows, headers, getTableProps, getHeaderProps, getRowProps }: any) => (
              <TableContainer>
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map((header: any) => (
                        <TableHeader {...getHeaderProps({ header })} key={header.key}>
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row: any) => (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>
                            {cell.info.header === 'tier' ? (
                              <Tag type={cell.value === 'hot' ? 'red' : cell.value === 'warm' ? 'cyan' : 'blue'}>
                                {cell.value.toUpperCase()}
                              </Tag>
                            ) : cell.info.header === 'storage' ? (
                              <Tag type="outline">{cell.value.toUpperCase()}</Tag>
                            ) : cell.info.header === 'cacheHitRate' && cell.value != null ? (
                              `${cell.value.toFixed(1)}%`
                            ) : cell.info.header === 'avgResponseTime' ? (
                              `${cell.value.toFixed(2)} ms`
                            ) : (
                              cell.value
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        </Tile>

        {/* Migration recommendations */}
        {migrationCandidates.length > 0 && (
          <Tile>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}
            >
              <Heading>Migration Recommendations</Heading>
              <Button onClick={() => setShowMigrationModal(true)} disabled={migrationCandidates.length === 0}>
                Process Migrations
              </Button>
            </div>
            <DataTable
              rows={migrationCandidates.map((m, i) => ({
                id: `${m.dataType}-${m.key}`,
                ...m,
              }))}
              headers={[
                { key: 'dataType', header: 'Data Type' },
                { key: 'currentTier', header: 'Current Tier' },
                { key: 'recommendedTier', header: 'Recommended Tier' },
                { key: 'accessCount', header: 'Access Count' },
                { key: 'reason', header: 'Reason' },
              ]}
            >
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }: any) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map((header: any) => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row: any) => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map((cell: any) => (
                            <TableCell key={cell.id}>
                              {cell.info.header.includes('Tier') ? (
                                <Tag type={cell.value === 'hot' ? 'red' : cell.value === 'warm' ? 'cyan' : 'blue'}>
                                  {cell.value.toUpperCase()}
                                </Tag>
                              ) : (
                                cell.value
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </Tile>
        )}
      </Stack>

      {/* Migration modal */}
      <Modal
        open={showMigrationModal}
        onRequestClose={() => setShowMigrationModal(false)}
        modalHeading="Process Tier Migrations"
        primaryButtonText="Start Migration"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleMigration}
      >
        <p style={{ marginBottom: '1rem' }}>
          Select the data items you want to migrate to their recommended tiers. Migrations will be queued and processed
          in the background.
        </p>
        <InlineNotification
          kind="info"
          title="Note"
          subtitle="Migrations may take several minutes to complete depending on data size."
          lowContrast
          hideCloseButton
        />
      </Modal>
    </div>
  );
};

export default DataRoutingDashboard;
